import { DragDropContext, DropResult, Droppable } from "@hello-pangea/dnd";
import { Grid, Typography } from "@mui/material";
import {
  ApiError,
  ApiErrorClass,
  Plan,
  PlanMeta,
  PlanPhase,
  PlanWeek,
  Profile,
} from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import moment from "moment";
import { useEffect, useState } from "react";
import { DumbbbellIcon, NoDataIcon } from "src/grading/assets";
import { LoadingButton } from "src/grading/components";
import PlanOverviewGraphs from "src/grading/components/PlanOverviewGraphs/PlanOverviewGraphs";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  useSortAthleteTrainingPlanMutation,
  useStartAssignedTrainingPlanMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import useStyles from "../AthleteDetails.styles";
import { ListWeeks } from "../ListWeeks/ListWeeks";
import TrainingCard from "../TrainingCard/TrainingCard";

interface TrainingTabProps {
  athlete: Profile | null;
  plan: Plan | null;
  meta: PlanMeta | null;
}

export const placeholderInitialValue = {
  clientX: 0,
  clientY: 0,
  clientHeight: 0,
  clientWidth: 0,
  destination: 0,
};

export const TrainingTab = ({ athlete, plan, meta }: TrainingTabProps) => {
  const [phases, setPhases] = useState<PlanPhase[]>(plan?.phases || []);
  const currentPhases = meta?.phases.current || 0;
  const currentWeeks = meta?.weeks.current || 0;
  const [showPrevWeeks, _setShowPrevWeeks] = useState(false);
  const [placeholderProps, setPlaceholderProps] = useState(
    placeholderInitialValue,
  );
  const [sortAthleteTrainingPlan] = useSortAthleteTrainingPlanMutation();
  const classes = useStyles();
  const [
    startAssignedTrainingPlan,
    { isLoading: isLoadingStartAssignedTrainingPlan },
  ] = useStartAssignedTrainingPlanMutation();
  const dispatch = useAppDispatch();

  const findPhase = (id: number) =>
    phases?.find((phase) => phase.numberOfPhase === id);
  const findWeek = (phase: PlanPhase, id: number) =>
    phase?.weeks?.find((i, index) => index === id);

  const onDragEnd = async (result: DropResult) => {
    setPlaceholderProps(placeholderInitialValue);
    const { source, destination } = result;
    if (
      !destination ||
      !source ||
      (destination.index === source.index &&
        destination.droppableId === source.droppableId)
    ) {
      return null;
    }

    const sourcePhase = findPhase(+source.droppableId);
    const destinationPhase = findPhase(+destination.droppableId);

    if (!sourcePhase || !destinationPhase) {
      return;
    }

    let newPhases: PlanPhase[] = [];
    let newWeeks: PlanWeek[] = [];

    if (+source.droppableId === +destination.droppableId) {
      const phase = findPhase(+source.droppableId);
      if (!phase) {
        return;
      }

      const fromPhaseWeek = findWeek(phase, source.index);
      const toPhaseWeek = findWeek(phase, destination.index);

      if (!fromPhaseWeek || !toPhaseWeek) {
        return;
      }

      const newFromPhaseWeek = {
        ...fromPhaseWeek,
        workouts: fromPhaseWeek.workouts.map((i, ind) => ({
          ...i,
          date: toPhaseWeek?.workouts[ind]?.date,
        })),
      };
      const newToPhaseWeek = {
        ...toPhaseWeek,
        workouts: toPhaseWeek?.workouts.map((i, ind) => ({
          ...i,
          date: fromPhaseWeek?.workouts[ind]?.date,
        })),
      };

      newWeeks = phase.weeks.map((week) =>
        week.id === newFromPhaseWeek.id
          ? newToPhaseWeek
          : week.id === newToPhaseWeek.id
            ? newFromPhaseWeek
            : week,
      );

      const weeks = newWeeks.map((week) => ({
        ...week,
        id: week.id,
        numberOfWeek: week.numberOfWeek,
      }));

      const newPhase = { ...phase, numberOfPhase: phase.numberOfPhase, weeks };

      newPhases = phases.map((phase) =>
        phase.numberOfPhase === newPhase.numberOfPhase
          ? {
              ...phase,
              numberOfPhase: newPhase.numberOfPhase,
              weeks: newPhase.weeks,
            }
          : { ...phase },
      );
    }

    setPhases(newPhases);

    const newPhaseOrder = newPhases.map((phase) => {
      return {
        id: phase.id,
        weeks: phase.weeks.map((week) => ({
          id: week.id,
        })),
      };
    });

    if (!plan?.id) {
      console.error("Plan ID is missing");
      return;
    }

    await sortAthleteTrainingPlan({ planId: plan?.id, phases: newPhaseOrder });
  };

  useEffect(() => {
    if (phases && phases.length) {
      const newPhases = phases.map((phase) => {
        const sortWeeks = [...(phase?.weeks ?? [])];
        sortWeeks?.sort((a, b) => a.numberOfWeek - b.numberOfWeek);

        const weeks = sortWeeks?.map((week) => {
          let startDate = moment(week?.start).format("YYYY-MM-DD");
          const workouts = [];

          for (let i = 0; i < 7; i++) {
            const filterWorkouts =
              week?.workouts?.filter(
                (j) => moment(j.date).format("YYYY-MM-DD") === startDate,
              ) || {};

            workouts.push(...filterWorkouts);

            startDate = moment(startDate).add(1, "days").format("YYYY-MM-DD");
          }

          return { ...week, workouts };
        });

        return { ...phase, weeks };
      });

      setPhases(newPhases);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update phases when plan changes
  useEffect(() => {
    if (plan?.phases) {
      setPhases(plan.phases);
    }
  }, [plan]);

  const listWeeks = (phase: PlanPhase) => {
    return (
      <Droppable
        droppableId={phase.numberOfPhase.toString()}
        key={phase.numberOfPhase}
      >
        {(provided, snapshot) => {
          return (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ position: "relative", width: "100%" }}
            >
              {phase.weeks.map((week, index) => {
                return (
                  <ListWeeks
                    key={week.numberOfWeek}
                    index={index}
                    week={week}
                    numberOfWeeks={meta?.weeks.total || 0}
                    currentWeeks={currentWeeks}
                    workouts={week?.workouts}
                  />
                );
              })}
              {provided.placeholder}
              {phase.numberOfPhase === +placeholderProps?.destination && (
                <div
                  style={{
                    top: placeholderProps.clientY,
                    left: placeholderProps.clientX,
                    height: placeholderProps.clientHeight,
                    width: placeholderProps.clientWidth,
                  }}
                />
              )}
            </div>
          );
        }}
      </Droppable>
    );
  };

  const handleStartAssignedTraining = async () => {
    try {
      if (!plan?.id) {
        throw new ApiErrorClass({
          message: "No plan id",
        });
      }

      await startAssignedTrainingPlan({ planId: plan?.id });
      dispatch(
        showSnackbar({
          open: true,
          message: "Plan started successfully",
        }),
      );
    } catch (error: unknown) {
      handleApiError(dispatch, error as ApiError);
    }
  };

  return (
    <Grid container>
      {/* Training Card */}
      <TrainingCard athlete={athlete} />

      {plan && athlete ? (
        <Grid container>
          {/* Plan Overview Graphs */}
          <PlanOverviewGraphs athlete={athlete} plan={plan} />

          {/* // Plan DND */}
          <Grid container className={classes.sectionContainerTraining}>
            <Grid item marginLeft={"auto"}>
              {!plan.isActive && (
                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={handleStartAssignedTraining}
                  className={classes.actionButton}
                  loading={isLoadingStartAssignedTrainingPlan}
                  label="Start Assigned Training"
                  icon={
                    <DumbbbellIcon style={{ marginLeft: theme.spacing(1) }} />
                  }
                />
              )}
            </Grid>
            <DragDropContext onDragEnd={onDragEnd}>
              {phases.map((phase) => {
                return (
                  (phase.numberOfPhase >= (currentPhases || 0) ||
                    showPrevWeeks) &&
                  listWeeks(phase)
                );
              })}
            </DragDropContext>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          className={`${classes.sectionContainer} ${classes.noTrainingDataContainer}`}
        >
          <Grid item xs={12}>
            <Typography variant="h5">Plan Overview</Typography>
          </Grid>

          <Grid
            container
            flexDirection={"column"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <NoDataIcon />
            <Typography variant="h5" fontWeight={"bold"}>
              There is no data to display yet.
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default TrainingTab;
